import { useState } from "react";

import { Box } from "@mui/material";

import { ChartPieSliceIcon } from "@phosphor-icons/react/dist/ssr";
import { doc, updateDoc } from "firebase/firestore";

import {
  DiagramModal,
  RegenerateModal,
  TabHeader,
} from "@/components/Luna/features";
import useLesson from "@/contexts/useLesson";
import useFirebaseContext from "@/hooks/useFirebaseAuth";
import { Diagram } from "@/types";
import { db } from "@/utils/firebase";

import { DiagramItem, DiagramSkeleton } from "./index";

const Diagrams = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { lessonInteractionProfile } = useLesson();
  const [selectedDiagram, setSelectedDiagram] = useState<Diagram | null>(null);
  const { aiAxios } = useFirebaseContext();
  const [isGenerating, setIsGenerating] = useState(false);
  const [regeneratingIndex, setRegeneratingIndex] = useState<number | null>(
    null
  );
  const [isRegenerateModalOpen, setIsRegenerateModalOpen] = useState(false);
  const diagrams = lessonInteractionProfile?.realtimeResources?.diagrams || [];

  const handleGenerateDiagram = async () => {
    if (!lessonInteractionProfile) return;
    setIsGenerating(true);
    aiAxios
      .post("luna/diagram", {
        lessonId: lessonInteractionProfile.id,
      })
      .finally(() => {
        setIsGenerating(false);
      });
  };

  const handleApproveDiagram = async (index: number) => {
    if (!lessonInteractionProfile) return;

    const diagramToApprove =
      lessonInteractionProfile.realtimeResources?.diagrams[index];
    if (!diagramToApprove) return;

    try {
      const lessonRef = doc(
        db,
        "lessonInteractionProfiles",
        lessonInteractionProfile.id
      );
      await updateDoc(lessonRef, {
        realtimeResources: {
          ...lessonInteractionProfile.realtimeResources,
          diagrams: lessonInteractionProfile.realtimeResources?.diagrams?.map(
            (diagram, i) =>
              i === index ? {
 ...diagram,
isApproved: true 
} : diagram
          ),
        },
      });
    } catch (error) {
      console.error("Error approving diagram:", error);
    }
  };

  const handleRegenerateDiagram = async (prompt: string) => {
    if (regeneratingIndex === null) return;
    const diagramToRegenerate =
      lessonInteractionProfile?.realtimeResources?.diagrams[regeneratingIndex];
    if (!diagramToRegenerate) return;

    aiAxios
      .put(`luna/diagram`, {
        lessonId: lessonInteractionProfile?.id,
        diagramId: diagramToRegenerate.id,
        prompt,
      })
      .finally(() => {
        setRegeneratingIndex(null);
      });
  };

  const handleDeleteDiagram = async (index: number) => {
    if (confirm("Are you sure you want to delete this diagram?")) {
      const diagramToDelete =
        lessonInteractionProfile?.realtimeResources?.diagrams[index];
      if (!diagramToDelete) return;

      try {
        const lessonRef = doc(
          db,
          "lessonInteractionProfiles",
          lessonInteractionProfile.id
        );
        await updateDoc(lessonRef, {
          realtimeResources: {
            ...lessonInteractionProfile.realtimeResources,
            diagrams:
              lessonInteractionProfile.realtimeResources?.diagrams?.filter(
                (_, i) => i !== index
              ),
          },
        });
      } catch (error) {
        console.error("Error deleting diagram:", error);
      }
    }
  };

  const openImageModal = (diagram: Diagram) => {
    setSelectedDiagram(diagram);
    setIsModalOpen(true);
  };

  const closeImageModal = () => {
    setSelectedDiagram(null);
    setIsModalOpen(false);
  };

  return (
    <Box
      width="100%"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <TabHeader
        icon={<ChartPieSliceIcon size={16} weight="bold" />}
        title="Diagrams"
        handleGenerate={handleGenerateDiagram}
        isGenerating={isGenerating}
      />
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          minHeight: 0,
        }}
      >
        {diagrams.map((diagram, index) =>
          regeneratingIndex === index ? (
            <DiagramSkeleton key={index} />
          ) : (
            <DiagramItem
              key={index}
              diagram={diagram}
              index={index}
              onApprove={handleApproveDiagram}
              onDelete={handleDeleteDiagram}
              onRegenerate={() => {
                setRegeneratingIndex(index);
                setIsRegenerateModalOpen(true);
              }}
              onOpenModal={openImageModal}
            />
          )
        )}
        {isGenerating && <DiagramSkeleton />}
      </Box>
      {selectedDiagram && (
        <DiagramModal
          isOpen={isModalOpen}
          onClose={closeImageModal}
          diagram={selectedDiagram}
        />
      )}
      {regeneratingIndex !== null && (
        <RegenerateModal
          isOpen={isRegenerateModalOpen}
          onClose={() => setIsRegenerateModalOpen(false)}
          onRegenerate={handleRegenerateDiagram}
        />
      )}
      {isGenerating && <DiagramSkeleton />}
    </Box>
  );
};

export default Diagrams;
